/**
 * Collapses concurrent calls of an async function into one.
 *
 * While a call is in flight every caller with the same key gets that same
 * promise; once it settles the next call starts fresh. Nothing is cached — the
 * only thing removed is the overlap, which is what stops N components mounting
 * in the same tick from firing N identical requests.
 *
 * Rejections propagate to every caller and clear the slot, so a failed call is
 * retried rather than remembered.
 *
 * @param {Function} fn      the async function to guard
 * @param {Function} [key]   maps the arguments to a dedup bucket; calls with
 *                           different keys run independently. Defaults to a
 *                           single shared bucket.
 */
export const singleFlight = (fn, key = () => '') => {
    const inflight = new Map();

    return (...args) => {
        const bucket = key(...args);

        if (inflight.has(bucket)) {
            return inflight.get(bucket);
        }

        // Wrapped so a synchronous throw inside `fn` becomes a rejection
        // instead of escaping before the slot is registered.
        const call = (async () => fn(...args))().finally(() => {
            inflight.delete(bucket);
        });

        inflight.set(bucket, call);

        return call;
    };
};
